import { TransactionStatus } from '../../graphql/generated';
import { addBusinessDays } from '../../util/businessDays';
import { toDollarsWithCents } from '../../util/currency';
import { dt, DT } from '../../util/dateTime';
import { useGoalAccount } from '../goals';
import {
    getProcessingDuration,
    GoalDirection,
    TransactionProps,
} from './index';

interface TransactionProgressInfo {
    stepConfigs: {
        title: string;
        description?: string;
    }[];
    numberOfStepsCompleted: number | null;
}

interface TransactionProgressMeta {
    amountStr: string;
    maxDate: DT;
    isTodayOnOrBeforeMaxExpectedDate: boolean;
    status: TransactionStatus;
}

export const useTransactionProgressInfo = ({
    transaction,
}: {
    transaction: TransactionProps;
}): TransactionProgressInfo | null => {
    const { sourceId, destinationId } = transaction;
    const sourceGoalAccount = useGoalAccount(sourceId);
    const destinationGoalAccount = useGoalAccount(destinationId);

    if (sourceGoalAccount && destinationGoalAccount) {
        // This is a goal-to-goal transfer
        // Choose progress trail based on destination goal account investment type
        const { investmentType } = destinationGoalAccount.info;
        const progressMeta = getTransactionProgressMeta(
            transaction,
            'GOAL_TO_GOAL'
        );
        return investmentType === 'HIGH_YIELD_CASH'
            ? getHighYieldCashDepositProgressInfo(progressMeta)
            : getPortfolioDepositProgressInfo(progressMeta);
    }

    if (sourceGoalAccount) {
        // This is a withdrawal from a goal
        // Choose progress trail based on source goal account investment type
        const { investmentType } = sourceGoalAccount.info;
        const progressMeta = getTransactionProgressMeta(
            transaction,
            'WITHDRAWAL'
        );
        return investmentType === 'HIGH_YIELD_CASH'
            ? getHighYieldCashWithdrawalProgressInfo(progressMeta)
            : getPortfolioWithdrawalProgressInfo(progressMeta);
    }

    if (destinationGoalAccount) {
        // This is a deposit into a goal
        // Choose progress trail based on destination goal account kind
        const { investmentType } = destinationGoalAccount.info;
        const progressMeta = getTransactionProgressMeta(transaction, 'DEPOSIT');
        return investmentType === 'HIGH_YIELD_CASH'
            ? getHighYieldCashDepositProgressInfo(progressMeta)
            : getPortfolioDepositProgressInfo(progressMeta);
    }

    return null;
};

const getTransactionProgressMeta = (
    transaction: TransactionProps,
    goalDirection: GoalDirection
): TransactionProgressMeta => {
    const amountStr = toDollarsWithCents(transaction.amount);
    const maxBusinessDays = getProcessingDuration(goalDirection).max;
    const maxDate = addBusinessDays(
        dt(transaction.plannedProcessingDate),
        maxBusinessDays
    );
    const isTodayOnOrBeforeMaxExpectedDate =
        dt().isSame(maxDate, 'day') || dt().isBefore(maxDate, 'day');
    return {
        amountStr,
        maxDate,
        isTodayOnOrBeforeMaxExpectedDate,
        status: transaction.status,
    };
};

const getPortfolioDepositProgressInfo = ({
    amountStr,
    maxDate,
    isTodayOnOrBeforeMaxExpectedDate,
    status,
}: TransactionProgressMeta): TransactionProgressInfo => {
    const stepConfigs = [
        {
            title: `${amountStr} transferred from source account`,
            description: '1 day',
        },
        {
            title: 'Funds used to purchase investments',
            description: 'Usually 1 day, could take up to 5',
        },
        {
            title: 'Trade settlement',
            description: '2 days',
        },
        {
            title: `${amountStr} added to your investment portfolio`,
            description:
                // If transaction is not yet completed
                // AND the max date has not yet passed,
                // show the "By ... at the latest" message.
                status !== 'COMPLETED' && isTodayOnOrBeforeMaxExpectedDate
                    ? `By ${maxDate.format('MMM D')} at the latest`
                    : '',
        },
    ];

    const stepsCompletedByStatus: Record<TransactionStatus, number | null> = {
        PLANNED: 0, // Plenty
        QUEUED: 0, // Plenty
        REQUESTING: 0, // Plenty
        EXTERNAL_ACCOUNT_BALANCE_UPDATE_REQUIRED: 0, // Atomic
        EXTERNAL_ACCOUNT_DISCONNECTED: 0, // Atomic
        EXTERNAL_ACCOUNT_INSUFFICIENT_BALANCE: 0, // Atomic
        EXTERNAL_ACCOUNT_MISSING: 0, // Atomic
        REQUESTED: 0, // Atomic
        SCHEDULED: 0, // Atomic
        PROCESSING: 1, // Atomic
        COMMITTED: 1, // Atomic
        DELAYED: 1, // Atomic (should not happen for deposits)
        TRADE_INITIATED: 2, // Atomic
        TRADE_SETTLED: 3, // Atomic
        TRANSFER_PLANNED: 3, // Plenty (only used in goal-to-goal)
        TRANSFER_QUEUED: 3, // Plenty (only used in goal-to-goal)
        TRANSFER_REQUESTING: 3, // Plenty (only used in goal-to-goal)
        TRANSFER_REQUESTED: 3, // Plenty (only used in goal-to-goal)
        COMPLETED: 4, // Atomic
        CANCELED: null, // Atomic
        EXPIRED: null, // Atomic
        FAILED: null, // Atomic
    };

    return {
        stepConfigs,
        numberOfStepsCompleted: stepsCompletedByStatus[status],
    };
};

const getHighYieldCashDepositProgressInfo = ({
    amountStr,
    status,
}: TransactionProgressMeta): TransactionProgressInfo => {
    const stepConfigs = [
        {
            title: `${amountStr} transferred from source account`,
            description: '1 day',
        },
        {
            title: `${amountStr} now earning Plenty's cash management rate`,
        },
    ];

    const stepsCompletedByStatus: Record<TransactionStatus, number | null> = {
        PLANNED: 0, // Plenty
        QUEUED: 0, // Plenty
        REQUESTING: 0, // Plenty
        EXTERNAL_ACCOUNT_BALANCE_UPDATE_REQUIRED: 0, // Atomic
        EXTERNAL_ACCOUNT_DISCONNECTED: 0, // Atomic
        EXTERNAL_ACCOUNT_INSUFFICIENT_BALANCE: 0, // Atomic
        EXTERNAL_ACCOUNT_MISSING: 0, // Atomic
        REQUESTED: 0, // Atomic
        SCHEDULED: 0, // Atomic
        PROCESSING: 1, // Atomic
        COMMITTED: 1, // Atomic
        DELAYED: 1, // Atomic (should not happen for deposits)
        TRADE_INITIATED: 1, // Atomic
        TRADE_SETTLED: 1, // Atomic
        TRANSFER_PLANNED: 1, // Plenty (only used in goal-to-goal)
        TRANSFER_QUEUED: 1, // Plenty (only used in goal-to-goal)
        TRANSFER_REQUESTING: 1, // Plenty (only used in goal-to-goal)
        TRANSFER_REQUESTED: 1, // Plenty (only used in goal-to-goal)
        COMPLETED: 2, // Atomic
        CANCELED: null, // Atomic
        EXPIRED: null, // Atomic
        FAILED: null, // Atomic
    };

    return {
        stepConfigs,
        numberOfStepsCompleted: stepsCompletedByStatus[status],
    };
};

const getPortfolioWithdrawalProgressInfo = ({
    amountStr,
    maxDate,
    isTodayOnOrBeforeMaxExpectedDate,
    status,
}: TransactionProgressMeta): TransactionProgressInfo => {
    const stepConfigs = [
        {
            title: `${amountStr} of investments sold from your portfolio`,
            description: 'Usually 1 day, could take up to 5',
        },
        {
            title: 'Trade settlement',
            description: '2 days',
        },
        {
            title: `${amountStr} transferred to destination`,
            description: '2-3 days',
        },
        {
            title: `${amountStr} arrives in your destination account`,
            description:
                // If transaction is not yet completed
                // AND the max date has not yet passed,
                // show the "By ... at the latest" message.
                status !== 'COMPLETED' && isTodayOnOrBeforeMaxExpectedDate
                    ? `By ${maxDate.format('MMM D')} at the latest`
                    : '',
        },
    ];

    const stepsCompletedByStatus: Record<TransactionStatus, number | null> = {
        PLANNED: 0, // Plenty
        QUEUED: 0, // Plenty
        REQUESTING: 0, // Plenty
        EXTERNAL_ACCOUNT_BALANCE_UPDATE_REQUIRED: 0, // Atomic
        EXTERNAL_ACCOUNT_DISCONNECTED: 0, // Atomic
        EXTERNAL_ACCOUNT_INSUFFICIENT_BALANCE: 0, // Atomic
        EXTERNAL_ACCOUNT_MISSING: 0, // Atomic
        REQUESTED: 0, // Atomic
        SCHEDULED: 0, // Atomic
        PROCESSING: 1, // Atomic
        COMMITTED: 1, // Atomic
        DELAYED: 1, // Atomic
        TRADE_INITIATED: 2, // Atomic
        TRADE_SETTLED: 2, // Atomic
        TRANSFER_PLANNED: 2, // Plenty
        TRANSFER_QUEUED: 2, // Plenty
        TRANSFER_REQUESTING: 2, // Plenty
        TRANSFER_REQUESTED: isTodayOnOrBeforeMaxExpectedDate ? 3 : 4, // Plenty
        COMPLETED: isTodayOnOrBeforeMaxExpectedDate ? 3 : 4, // Atomic
        CANCELED: null, // Atomic
        EXPIRED: null, // Atomic
        FAILED: null, // Atomic
    };

    return {
        stepConfigs,
        numberOfStepsCompleted: stepsCompletedByStatus[status],
    };
};

const getHighYieldCashWithdrawalProgressInfo = ({
    amountStr,
    maxDate,
    isTodayOnOrBeforeMaxExpectedDate,
    status,
}: TransactionProgressMeta): TransactionProgressInfo => {
    const stepConfigs = [
        {
            title: `${amountStr} of MMF portfolio holdings sold`,
            description: 'Usually 1 day',
        },
        {
            title: `${amountStr} transferred to destination`,
            description: '2-3 days',
        },
        {
            title: `${amountStr} arrives in your destination account`,
            description:
                // If transaction is not yet completed
                // AND the max date has not yet passed,
                // show the "By ... at the latest" message.
                status !== 'COMPLETED' && isTodayOnOrBeforeMaxExpectedDate
                    ? `By ${maxDate.format('MMM D')} at the latest`
                    : '',
        },
    ];

    const stepsCompletedByStatus: Record<TransactionStatus, number | null> = {
        PLANNED: 0, // Plenty
        QUEUED: 0, // Plenty
        REQUESTING: 0, // Plenty
        REQUESTED: 0, // Atomic
        SCHEDULED: 0, // Atomic
        PROCESSING: 1, // Atomic
        COMMITTED: 1, // Atomic
        TRADE_INITIATED: 1, // Atomic
        TRADE_SETTLED: 1, // Atomic
        TRANSFER_PLANNED: 1, // Plenty
        TRANSFER_QUEUED: 1, // Plenty
        TRANSFER_REQUESTING: 1, // Plenty
        EXTERNAL_ACCOUNT_BALANCE_UPDATE_REQUIRED: 1, // Atomic
        EXTERNAL_ACCOUNT_DISCONNECTED: 1, // Atomic
        EXTERNAL_ACCOUNT_INSUFFICIENT_BALANCE: 1, // Atomic
        EXTERNAL_ACCOUNT_MISSING: 1, // Atomic
        TRANSFER_REQUESTED: isTodayOnOrBeforeMaxExpectedDate ? 2 : 3, // Plenty
        DELAYED: 2, // Atomic
        COMPLETED: isTodayOnOrBeforeMaxExpectedDate ? 2 : 3, // Atomic
        CANCELED: null, // Atomic
        EXPIRED: null, // Atomic
        FAILED: null, // Atomic
    };

    return {
        stepConfigs,
        numberOfStepsCompleted: stepsCompletedByStatus[status],
    };
};
